import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PageContainer from '../../../shared/PageContainer';
import GlowCard from '../../../shared/GlowCard';
import CyberpunkTitle from '../../../shared/CyberpunkTitle';
import { useColors } from '../../../theme/ColorModeContext';
import { getTheoryStageQuestions } from '../../../api/Api';
import { getAcademyTrackForCategory, categoryTitles } from '../skillTreeData';
import QuizQuestion from './QuizQuestion';
import { getStageContent } from './stageRegistry';
import { TheoryQuestionData } from './types';
import DiscordCTA from '../../../shared/DiscordCTA';

interface PropsTheoryStage {
    token: string;
    category: string;
}

type LockedState = 'locked-anon' | 'locked-progress' | null;

const TheoryStage = ({ token, category }: PropsTheoryStage) => {
    const colors = useColors();
    const navigate = useNavigate();
    const { stageSlug } = useParams();
    const track = getAcademyTrackForCategory(category);

    const [loading, setLoading] = useState(true);
    const [lockedState, setLockedState] = useState<LockedState>(null);
    const [stageTitle, setStageTitle] = useState('');
    const [questions, setQuestions] = useState<TheoryQuestionData[]>([]);
    const [answeredMap, setAnsweredMap] = useState<Record<number, boolean>>({});
    const [stageCompleted, setStageCompleted] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLockedState(null);

        const fetchData = async () => {
            const response = await getTheoryStageQuestions(token, category, stageSlug);
            if (cancelled) return;

            if (!response) {
                setLoading(false);
                return;
            }

            if (response.status === 403) {
                setLockedState(token ? 'locked-progress' : 'locked-anon');
                setLoading(false);
                return;
            }

            if (response.status === 404) {
                navigate(`/academy/${category}`);
                return;
            }

            if (response.status === 200) {
                const data = response.data;
                const fetchedQuestions: TheoryQuestionData[] = data.questions;
                setStageTitle(data.stage.title);
                setQuestions(fetchedQuestions);

                const initialAnswered: Record<number, boolean> = {};
                fetchedQuestions.forEach((question, idx) => {
                    initialAnswered[idx] = question.answered;
                });
                setAnsweredMap(initialAnswered);
                setStageCompleted(fetchedQuestions.length > 0 && fetchedQuestions.every((question) => question.answered));
            }

            setLoading(false);
        };

        fetchData();
        return () => { cancelled = true; };
    }, [token, category, stageSlug]);

    const stageContent = stageSlug ? getStageContent(category, stageSlug) : undefined;

    const handleCorrect = (n: number, completed: boolean) => {
        setAnsweredMap((prev) => ({ ...prev, [n]: true }));
        if (completed) setStageCompleted(true);
    };

    if (loading) {
        return (
            <PageContainer>
                <Box sx={{ textAlign: 'center', marginTop: '80px' }}>
                    <CircularProgress size={60} />
                </Box>
            </PageContainer>
        );
    }

    if (lockedState) {
        return (
            <PageContainer maxWidth="sm">
                <GlowCard glowOnHover={false} gradientBorder>
                    <Box sx={{ p: '40px', textAlign: 'center' }}>
                        <LockIcon sx={{ fontSize: 44, color: colors.text.muted, mb: 2 }} />
                        <Typography variant="h5" sx={{ fontFamily: '"Orbitron", sans-serif', fontWeight: 700, color: colors.text.primary, mb: 1.5 }}>
                            This stage is locked
                        </Typography>
                        <Typography sx={{ color: colors.text.secondary, mb: 3 }}>
                            {lockedState === 'locked-anon'
                                ? `Sign up for a free account to keep going with ${categoryTitles[category] || 'Ethereum'} Network Basics.`
                                : "Finish the previous stage's quiz first to unlock this one."}
                        </Typography>
                        <Button
                            component={RouterLink}
                            to={lockedState === 'locked-anon' ? '/register' : `/academy/${category}`}
                            variant="outlined"
                            sx={{ borderColor: colors.accent.cyan, color: colors.accent.cyan }}
                        >
                            {lockedState === 'locked-anon' ? 'Sign up' : 'Back to stages'}
                        </Button>
                    </Box>
                </GlowCard>
            </PageContainer>
        );
    }

    if (!stageContent) {
        return (
            <PageContainer>
                <Typography sx={{ color: colors.text.secondary }}>This stage doesn't have content configured yet.</Typography>
            </PageContainer>
        );
    }

    const rendered: React.ReactNode[] = [];
    let gated = false;
    for (const block of stageContent.blocks) {
        if (gated) break;

        if (block.type === 'content') {
            rendered.push(<Box key={block.key}>{block.render()}</Box>);
            continue;
        }

        const question = questions[block.n];
        if (!question) {
            gated = true;
            break;
        }

        rendered.push(
            <QuizQuestion
                key={question.id}
                token={token}
                category={category}
                stageSlug={stageSlug || ''}
                question={question}
                solvedAlready={!!answeredMap[block.n]}
                onCorrect={(completed) => handleCorrect(block.n, completed)}
            />
        );

        if (!answeredMap[block.n]) {
            gated = true;
        }
    }

    return (
        <PageContainer maxWidth="lg">
            <CyberpunkTitle
                segments={[
                    { label: track.title, to: token ? `/academy/${track.slug}` : undefined },
                    { label: categoryTitles[category] || 'Network Basics', to: `/academy/${category}` },
                    { label: stageTitle },
                ]}
                mb="24px"
            />

            {rendered}

            {stageCompleted && !gated && (
                <GlowCard glowOnHover={false} gradientBorder>
                    <Box sx={{ p: '32px', textAlign: 'center' }}>
                        <CheckCircleIcon sx={{ fontSize: 44, color: colors.accent.green, mb: 2 }} />
                        <Typography variant="h5" sx={{ fontFamily: '"Orbitron", sans-serif', fontWeight: 700, color: colors.text.primary, mb: 1.5 }}>
                            Stage complete
                        </Typography>
                        <Typography sx={{ color: colors.text.secondary, mb: 3 }}>
                            Nice work. The next stage is unlocked.
                        </Typography>
                        <Button
                            component={RouterLink}
                            to={`/academy/${category}`}
                            variant="outlined"
                            sx={{ borderColor: colors.accent.cyan, color: colors.accent.cyan }}
                        >
                            Back to stages
                        </Button>
                        <DiscordCTA variant="reward" placement="stage-complete" sx={{ mt: 3 }} />
                    </Box>
                </GlowCard>
            )}
        </PageContainer>
    );
};

export default TheoryStage;
